import { useState } from "react";
import { motion } from "framer-motion";
import { Wand2, ArrowRight, Palette, Type, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const colorThemes = [
  { name: "Indigo", primary: "#6366f1", accent: "#f97316", bg: "#0a0a1a" },
  { name: "Émeraude", primary: "#10b981", accent: "#6366f1", bg: "#0a1a14" },
  { name: "Rose", primary: "#ec4899", accent: "#8b5cf6", bg: "#1a0a14" },
  { name: "Bleu Ocean", primary: "#3b82f6", accent: "#06b6d4", bg: "#0a0f1a" },
  { name: "Or & Noir", primary: "#eab308", accent: "#f59e0b", bg: "#0f0f0f" },
];

const jobs = [
  "Consultant", "Designer", "Photographe", "Coach", "Développeur",
  "Artisan", "Avocat", "Architecte", "Formateur", "Agent immobilier",
];

const SellPersonalizer = () => {
  const [name, setName] = useState("");
  const [job, setJob] = useState("");
  const [activeTheme, setActiveTheme] = useState(0);

  const theme = colorThemes[activeTheme];
  const displayName = name || "Votre Nom";
  const displayJob = job || "Votre métier";

  return (
    <section className="py-24 relative overflow-hidden" id="personalizer">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-accent/[0.03] to-transparent" />
      <div className="container mx-auto px-6 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-14"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
            <Wand2 className="w-4 h-4" />
            Testez en live
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            Visualisez{" "}
            <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              votre portfolio
            </span>
            {" "}en temps réel
          </h2>
          <p className="text-muted-foreground text-lg">
            Entrez votre nom et métier, choisissez un thème — voyez le résultat instantanément
          </p>
        </motion.div>

        <div className="max-w-6xl mx-auto grid lg:grid-cols-[380px_1fr] gap-8 items-start">
          {/* Controls */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="p-6 rounded-2xl bg-card/60 backdrop-blur-sm border border-border/50 space-y-6 sticky top-24"
          >
            <div>
              <label className="text-sm font-semibold mb-2 block flex items-center gap-2">
                <Type className="w-4 h-4 text-primary" />
                Votre nom
              </label>
              <Input
                placeholder="ex: Marie Dupont"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="py-5"
              />
            </div>

            <div>
              <label className="text-sm font-semibold mb-2 block flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                Votre métier
              </label>
              <Input
                placeholder="ex: Consultante en stratégie"
                value={job}
                onChange={(e) => setJob(e.target.value)}
                className="py-5"
              />
              <div className="flex flex-wrap gap-1.5 mt-2">
                {jobs.map((j) => (
                  <button
                    key={j}
                    onClick={() => setJob(j)}
                    className={`px-2.5 py-1 rounded-lg text-xs transition-colors border ${
                      job === j
                        ? "bg-primary/10 border-primary/30 text-primary font-medium"
                        : "border-border/50 text-muted-foreground hover:border-primary/30"
                    }`}
                  >
                    {j}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold mb-3 block flex items-center gap-2">
                <Palette className="w-4 h-4 text-primary" />
                Palette de couleurs
              </label>
              <div className="grid grid-cols-5 gap-2">
                {colorThemes.map((t, i) => (
                  <button
                    key={t.name}
                    onClick={() => setActiveTheme(i)}
                    className={`flex flex-col items-center gap-1.5 p-2 rounded-xl border transition-all ${
                      activeTheme === i
                        ? "border-primary bg-primary/5 scale-105"
                        : "border-border/50 hover:border-primary/30"
                    }`}
                  >
                    <div
                      className="w-8 h-8 rounded-lg shadow-sm"
                      style={{ background: `linear-gradient(135deg, ${t.primary}, ${t.accent})` }}
                    />
                    <span className="text-xs font-medium text-muted-foreground leading-tight">{t.name}</span>
                  </button>
                ))}
              </div>
            </div>

            <a href="#pricing">
              <Button size="lg" className="w-full bg-gradient-to-r from-primary to-accent hover:opacity-90 shadow-lg shadow-primary/25 group py-6 mt-2">
                Créer le mien maintenant
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
          </motion.div>

          {/* Live Preview */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="rounded-2xl border border-border/50 overflow-hidden shadow-2xl">
              {/* Browser chrome */}
              <div className="flex items-center gap-2 px-4 py-3 bg-secondary/60 border-b border-border/40">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-destructive/60" />
                  <div className="w-3 h-3 rounded-full bg-enterprise-warning/60" />
                  <div className="w-3 h-3 rounded-full bg-enterprise-success/60" />
                </div>
                <div className="flex-1 mx-3">
                  <div className="h-6 rounded-lg bg-background/80 flex items-center px-3">
                    <span className="text-xs text-muted-foreground font-mono">
                      🔒 {name ? name.toLowerCase().replace(/\s+/g, "-") : "votre-nom"}.com
                    </span>
                  </div>
                </div>
              </div>

              {/* Portfolio preview */}
              <div
                className="p-6 md:p-8 min-h-[500px] transition-colors duration-500"
                style={{ backgroundColor: theme.bg }}
              >
                {/* Nav */}
                <div className="flex items-center justify-between mb-8">
                  <div
                    className="h-5 w-24 sm:w-32 rounded-lg flex-shrink-0"
                    style={{ background: `linear-gradient(90deg, ${theme.primary}, ${theme.accent})` }}
                  />
                  <div className="flex gap-2 sm:gap-4 overflow-hidden">
                    {["À propos", "Projets"].map((n) => (
                      <span key={n} className="text-[10px] sm:text-xs font-medium whitespace-nowrap" style={{ color: theme.primary + "80" }}>{n}</span>
                    ))}
                  </div>
                </div>

                {/* Hero */}
                <div className="grid grid-cols-[1fr_auto] gap-8 items-center mb-8">
                  <div className="space-y-3">
                    <p className="text-xs font-mono uppercase tracking-widest" style={{ color: theme.primary }}>
                      {displayJob}
                    </p>
                    <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white leading-tight">
                      {displayName.split(" ")[0]}
                      <br />
                      <span style={{ color: theme.primary }}>
                        {displayName.split(" ").slice(1).join(" ") || "Portfolio"}
                      </span>
                    </h1>
                    <p className="text-sm text-gray-400 leading-relaxed max-w-sm">
                      {displayJob !== "Votre métier"
                        ? `${displayJob} passionné(e), je mets mon expertise au service de vos projets.`
                        : "Découvrez mon parcours, mes réalisations et ce que je peux apporter à votre projet."
                      }
                    </p>
                    <div className="flex gap-3 pt-2">
                      <div
                        className="h-9 px-5 rounded-lg flex items-center text-xs font-semibold text-white"
                        style={{ background: `linear-gradient(135deg, ${theme.primary}, ${theme.accent})` }}
                      >
                        Me contacter
                      </div>
                      <div
                        className="h-9 px-5 rounded-lg flex items-center text-xs font-semibold border"
                        style={{ borderColor: theme.primary + "40", color: theme.primary }}
                      >
                        Voir mes projets
                      </div>
                    </div>
                  </div>
                  <div
                    className="w-24 h-24 md:w-32 md:h-32 rounded-2xl border-2"
                    style={{
                      borderColor: theme.primary,
                      background: `linear-gradient(135deg, ${theme.primary}30, ${theme.accent}20)`,
                    }}
                  />
                </div>

                {/* Skills cards */}
                <div className="grid grid-cols-3 gap-3 mb-6">
                  {["Expertise 1", "Expertise 2", "Expertise 3"].map((s, i) => (
                    <div
                      key={s}
                      className="rounded-xl p-4 space-y-2 border"
                      style={{
                        borderColor: theme.primary + "20",
                        background: `linear-gradient(135deg, ${theme.primary}15, ${theme.accent}10)`,
                      }}
                    >
                      <div
                        className="w-8 h-8 rounded-lg"
                        style={{ background: theme.primary + "30" }}
                      />
                      <div className="h-2 w-3/4 rounded" style={{ background: theme.primary + "25" }} />
                      <div className="h-1.5 w-full rounded" style={{ background: theme.primary + "15" }} />
                    </div>
                  ))}
                </div>

                {/* Chart */}
                <div
                  className="rounded-xl p-4 flex items-end gap-2 h-24 border"
                  style={{ borderColor: theme.primary + "15", background: theme.bg }}
                >
                  {[40, 60, 80, 50, 90, 70, 85, 55, 95].map((h, i) => (
                    <motion.div
                      key={i}
                      className="flex-1 rounded-t"
                      style={{ background: `linear-gradient(to top, ${theme.primary}, ${theme.accent}90)` }}
                      initial={{ height: 0 }}
                      whileInView={{ height: `${h}%` }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 + i * 0.05, duration: 0.5 }}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Theme label */}
            <motion.div
              className="mt-4 flex items-center justify-center gap-2 text-sm text-muted-foreground"
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ repeat: Infinity, duration: 2 }}
            >
              <Palette className="w-4 h-4" />
              Thème actuel : <strong className="text-foreground">{theme.name}</strong>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default SellPersonalizer;
